import type { Answer } from '../domain/facts';
import type { Mode, Question } from '../domain/questions';
import { confirmAction, pointerCanHover } from './confirm';
import { el } from './dom';
import { pinIcon } from './icons';

export interface QuestionViewProps {
  question: Question;
  asked: number;
  estimatedTotal: number;
  locationLabel: string;
  mode: Mode;
  canGoBack: boolean;
  onAnswer: (choice: Answer) => void;
  onBack: () => void;
  onSkipToResults: () => void;
  onChangeLocation: () => void;
  onToggleMode: () => void;
}

const RESULTS_HINT =
  'Searches now with what you have answered - the rest of the questions are dropped.';

const MODE_HINT =
  'Asks a few more, narrowing to a specific dish rather than a kind of food. Nothing already answered is lost.';

/**
 * Wraps a button with an explanation that shows on hover or keyboard focus.
 *
 * The bubble is real text in the DOM rather than a `title` attribute, so
 * `aria-describedby` can point at it and a screen reader reads the same sentence a
 * mouse user sees. CSS decides whether it is ever visible.
 */
function hinted(id: string, hint: string, button: HTMLButtonElement): HTMLElement {
  button.setAttribute('aria-describedby', id);
  return el('div', { class: 'hint' }, button, el('span', { class: 'hint__bubble', id }, hint));
}

export function questionView(props: QuestionViewProps): HTMLElement {
  const dots = el(
    'div',
    { class: 'progress', 'aria-hidden': 'true' },
    ...Array.from({ length: props.estimatedTotal }, (_, i) =>
      el('span', { class: i < props.asked ? 'done' : '' }),
    ),
  );

  const answer = (choice: Answer, label: string, variant: string) =>
    el(
      'button',
      { class: `btn ${variant}`, type: 'button', onclick: () => props.onAnswer(choice) },
      label,
    );

  /**
   * Both hinted buttons close something off, and neither is obvious from its label,
   * so where the hint cannot be read the action asks first instead of just happening.
   */
  const guarded = (
    label: string,
    hint: string,
    dialog: { title: string; confirmLabel: string },
    act: () => void,
  ) =>
    el(
      'button',
      {
        class: 'btn btn--ghost',
        type: 'button',
        onclick: () => {
          if (pointerCanHover()) {
            act();
            return;
          }
          confirmAction({ ...dialog, body: hint, onConfirm: act });
        },
      },
      label,
    );

  const skipHint = props.question.skipHint;

  return el(
    'div',
    { class: 'grow' },
    // Left out rather than disabled on the first question: a button that can't do
    // anything is worse than one more row appearing once there's something to undo.
    props.canGoBack &&
      el(
        'div',
        { class: 'question__nav' },
        el('button', { class: 'linkish', type: 'button', onclick: props.onBack }, '← Back'),
      ),
    dots,
    el('h2', { class: 'question__prompt' }, props.question.prompt),
    el(
      'div',
      { class: 'question__answers' },
      answer('yes', props.question.labels.yes, 'btn--primary'),
      answer('no', props.question.labels.no, ''),
    ),
    // "Skip" closes the whole axis rather than deferring the question, which is what
    // makes it meaningfully different from "no" - and is what the hint spells out.
    hinted(
      'hint-skip',
      skipHint,
      guarded(
        "Don't mind",
        skipHint,
        { title: "Don't mind?", confirmLabel: "Don't mind" },
        () => props.onAnswer('skip'),
      ),
    ),
    hinted(
      'hint-results',
      RESULTS_HINT,
      guarded(
        'Just give me something →',
        RESULTS_HINT,
        { title: 'Search now?', confirmLabel: 'Search now' },
        props.onSkipToResults,
      ),
    ),
    el(
      'div',
      { class: 'question__links' },
      el(
        'button',
        { class: 'linkish linkish--icon', type: 'button', onclick: props.onChangeLocation },
        pinIcon(),
        // The label carries the underline rather than the button, so the rule stops at
        // the words instead of running under the pin.
        el('span', {}, `Near ${props.locationLabel} — change`),
      ),
      // Safe to offer mid-run: the simple question set is a subset of the advanced one,
      // so switching either way keeps every answer already given. A switch rather than
      // a link because it reports a state as well as offering an action - a link had no
      // way to say which mode you were already in.
      hinted(
        'hint-mode',
        MODE_HINT,
        el(
          'button',
          {
            class: 'switch',
            type: 'button',
            role: 'switch',
            'aria-checked': String(props.mode === 'advanced'),
            onclick: props.onToggleMode,
          },
          'More questions',
          el(
            'span',
            { class: 'switch__track', 'aria-hidden': 'true' },
            el('span', { class: 'switch__thumb' }),
          ),
        ),
      ),
    ),
  );
}
